import { Link } from '@inertiajs/react';
import { useTranslation } from 'react-i18next';
import TeacherShell, { TeacherPageHeader } from '../../../components/layout/TeacherShell';
import { Card, Button } from '../../../components/ui';
import { Section, EmptyState } from '../../../components/layout/AppShell';
import { cn } from '../../../lib/cn';

export default function TeacherActivitiesIndex({ activities }) {
  const { t } = useTranslation();
  const draftActivities = activities.filter(l => l.status === 'draft');
  const readyActivities = activities.filter(l => l.status === 'ready');
  const activeActivities = activities.filter(l => l.status === 'active');
  const archivedActivities = activities.filter(l => l.status === 'archived');

  return (
    <TeacherShell scrollTitle={t('teacherActivities.title')}>
      <TeacherPageHeader
        title={t('teacherActivities.title')}
        subtitle={t('teacherActivities.subtitle')}
        actions={
          <Link href="/teacher/activities/new">
            <Button variant="primary">
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t('teacherActivities.newActivity')}
            </Button>
          </Link>
        }
      />

      {activities.length === 0 ? (
        <EmptyState
          title={t('teacherActivities.noActivities')}
          description={t('teacherActivities.noActivitiesHelp')}
          action={
            <Link href="/teacher/activities/new">
              <Button variant="primary">{t('teacherActivities.createFirstActivity')}</Button>
            </Link>
          }
        />
      ) : (
        <div className="space-y-10">
          {draftActivities.length > 0 && (
            <Section title={t('teacherActivities.drafts')} description={t('teacherActivities.draftsDesc')}>
              <ActivityGrid activities={draftActivities} />
            </Section>
          )}

          {readyActivities.length > 0 && (
            <Section title={t('teacherActivities.ready')} description={t('teacherActivities.readyDesc')}>
              <ActivityGrid activities={readyActivities} />
            </Section>
          )}

          {activeActivities.length > 0 && (
            <Section title={t('teacherActivities.active')} description={t('teacherActivities.activeDesc')}>
              <ActivityGrid activities={activeActivities} />
            </Section>
          )}

          {archivedActivities.length > 0 && (
            <Section title={t('common.archived')}>
              <ActivityGrid activities={archivedActivities} />
            </Section>
          )}
        </div>
      )}
    </TeacherShell>
  );
}

function ActivityGrid({ activities }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {activities.map((activity) => (
        <ActivityCard key={activity.id} activity={activity} />
      ))}
    </div>
  );
}

function ActivityCard({ activity }) {
  const { t } = useTranslation();
  const statusColors = {
    draft: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    ready: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    archived: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
  };

  return (
    <Link href={`/teacher/activities/${activity.id}/edit`}>
      <Card className="p-5 hover:shadow-md transition-shadow cursor-pointer h-full flex flex-col">
        <div className="flex items-start justify-between mb-3">
          <span className={cn(
            'text-xs font-medium px-2 py-0.5 rounded',
            statusColors[activity.status] || statusColors.draft
          )}>
            {activity.status || 'draft'}
          </span>
          {activity.stimulus_type && (
            <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">
              {activity.stimulus_type}
            </span>
          )}
        </div>

        <h3 className="font-semibold text-gray-900 dark:text-white mb-1 line-clamp-2">
          {activity.title}
        </h3>

        {activity.description && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-3 line-clamp-2 flex-1">
            {activity.description}
          </p>
        )}

        <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400 mt-auto pt-3 border-t border-gray-100 dark:border-gray-700">
          <span className={cn(
            activity.approved_count === 0 && 'text-yellow-600 dark:text-yellow-400'
          )}>
            {activity.approved_count}/{activity.question_count} {t('teacherActivities.questionsApproved')}
          </span>
          {activity.session_count > 0 && (
            <span>{activity.session_count} {activity.session_count !== 1 ? t('common.sessions') : t('common.session')}</span>
          )}
        </div>
      </Card>
    </Link>
  );
}
